import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePagination } from "@components/index";
// import { FIELDS_VOUCHERS } from "@constants/misDownloadData";
// import { useCSVExport } from "@hooks/useCSVExport";
import { getAllVouchersList } from "@actions/fetchVoucherList";
import { formatDateToISOstring, isValidDateRange } from "@utils/index";
import { useAppConfig } from "@src/context/AppConfigContext";

const fetchVouchersData = async (payload) => {
  try {
    const response = await getAllVouchersList(payload);

    if (response.success) {
      return {
        success: true,
        data: response.data.vouchers,
        error: null,
      };
    } else {
      return {
        success: false,
        data: [],
        error: response.message,
      };
    }
  } catch (err) {
    return {
      success: false,
      data: [],
      error: "Failed to fetch vouchers",
    };
  }
};

export const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

export const searchOptions = [
  {
    value: "global",
    label: "Search All Fields",
    placeholder: "Search across all vouchers data (PNR, Voucher Code, etc.)",
  },
];

// const mapVoucherToData = (voucher, index) => ({
//   "SR. No": index + 1,
//   "voucher code ": voucher.voucher_code || "-",
//   "PNR Number": voucher.old_pnr_number || "-",
//   "Partner ID": voucher.partner_id || "-",
//   "Refund Amount": voucher.refund_amount || 0,
//   "Redeemed Amount": voucher.voucher_redeem_amount || 0,
// });

export const useVouchersData = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isInitialMount = useRef(true);
  const { selectedClient } = useAppConfig();

  // XLSX export hook
  // const { exportToXLSX, generateDateFilename } = useCSVExport();

  // Load vouchers data
  const loadVouchersDataHook = useCallback(
    async (payload) => {
      setLoading(true);

      const reqPayload = {
        ...payload,
        clientname: selectedClient?.clientName,
      };

      const result = await fetchVouchersData(reqPayload);

      setVouchers(result.data || []);
      setError(result.error);
      setLoading(false);
    },
    [selectedClient?.clientName],
  );

  // Initial data load
  useEffect(() => {
    if (isInitialMount.current) {
      const payload = {
        startDate: formatDateToISOstring(new Date()),
        endDate: formatDateToISOstring(new Date()),
      };

      isInitialMount.current = false;
      loadVouchersDataHook(payload);
    } else {
      if (isValidDateRange(fromDate, toDate, false)) {
        const payload = {
          startDate: formatDateToISOstring(fromDate),
          endDate: formatDateToISOstring(toDate),
        };

        loadVouchersDataHook(payload);
      }
    }
  }, [fromDate, loadVouchersDataHook, toDate]);

  const filterVouchers = (vouchers, searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      return vouchers;
    }

    const lowerSearchValue = searchValue.toLowerCase().trim();

    return vouchers.filter((voucher) => {
      // Define all searchable fields
      const searchableFields = [
        voucher.pnr_number,
        voucher.voucher_code,
        voucher.voucher_redeem_status,
      ];

      // Search across all fields
      return searchableFields.some(
        (field) =>
          field && field.toString().toLowerCase().includes(lowerSearchValue),
      );
    });
  };

  // Handle Voucher view
  const handleViewVoucher = useCallback((Voucher) => {
    setSelectedVoucher(Voucher);
    setIsModalOpen(true);
  }, []);

  // Filter vouchers based on search
  const filteredVouchers = useMemo(() => {
    return filterVouchers(vouchers, searchValue);
  }, [vouchers, searchValue]);

  // Handle search
  const handleSearch = useCallback((_, searchValue) => {
    setSearchValue(searchValue);
  }, []);

  // Handle date range change
  const handleDateChange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFromDate(new Date());
    setToDate(new Date());
    setSearchValue("");
  }, []);

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedVouchers,
    handlePageChange,
  } = usePagination(filteredVouchers, 10);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVoucher(null);
  }, []);

  // const handleDownloadVoucherData = useCallback(() => {
  //   const filename = generateDateFilename("Voucher_Data");
  //   exportToXLSX(
  //     filteredVouchers,
  //     FIELDS_VOUCHERS,
  //     filename,
  //     mapVoucherToData,
  //     "Vouchers",
  //   );
  // }, [exportToXLSX, generateDateFilename, filteredVouchers]);

  return {
    // Data
    vouchers: paginatedVouchers, // Paginated vouchers for display
    allVouchers: filteredVouchers, // All filtered vouchers for pagination component
    loading,
    error,

    // Search & Filters
    searchValue,
    handleSearch,
    fromDate,
    toDate,
    handleDateChange,
    handleClearFilters,

    // Pagination
    currentPage,
    handlePageChange,

    // Data loading
    loadVouchersData: loadVouchersDataHook,

    // Modal view
    handleViewVoucher,
    selectedVoucher,
    isModalOpen,
    handleCloseModal,

    // download voucher data
    // handleDownloadVoucherData,
  };
};
